import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, AlertTriangle, MapPin, ClipboardList, MessagesSquare, Camera, Users, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { SERVICES, IMG } from "@/lib/constants";

function Hero() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <section className="relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">
            <MapPin className="h-3.5 w-3.5" />{t("hero.badge")}
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900" data-testid="hero-title">{t("hero.title")}</h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">{t("hero.sub")}</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center sm:justify-start justify-center gap-3" data-testid="hero-cta-group">
            <Button size="lg" onClick={() => nav("/request")} className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-12 px-7" data-testid="hero-request-btn">
              {t("hero.cta1")}<ArrowRight className="h-4 w-4 ms-2 rtl:rotate-180" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => nav("/join")} className="rounded-full h-12 px-7 border-slate-300" data-testid="hero-join-btn">{t("hero.cta2")}</Button>
          </div>
        </div>
        <div className="relative fade-up" style={{ animationDelay: ".15s" }}>
          <div className="absolute -inset-4 bg-teal-600 rounded-[2rem] rotate-3" />
          <img src={IMG.hero} alt="NicaFix professionals greeting a homeowner in San Juan del Sur, Nicaragua" className="relative rounded-[1.75rem] h-[300px] sm:h-[420px] w-full object-cover object-[70%_center]" />
          <div className="absolute -bottom-6 start-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-slate-100">
            <Users className="h-8 w-8 text-teal-600" />
            <p className="text-sm font-semibold text-slate-800 max-w-[240px]" data-testid="hero-image-box">{t("hero.box")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const ICON_COLORS = [
  "from-amber-400 to-orange-500", "from-sky-400 to-blue-600", "from-rose-400 to-red-500", "from-emerald-400 to-green-600",
  "from-violet-400 to-purple-600", "from-cyan-400 to-teal-600", "from-pink-400 to-fuchsia-600", "from-yellow-300 to-amber-500",
  "from-indigo-400 to-blue-700", "from-lime-400 to-emerald-600", "from-orange-400 to-rose-500",
];

export function ServiceGrid() {
  const { t } = useI18n();
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setActive(r.data.categories)); }, []);
  if (!active) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="service-grid">
      {SERVICES.filter((s) => active.includes(s.key)).map(({ key, icon: Icon }, i) => (
        <Link key={key} to={`/request?service=${key}`} data-testid={`service-card-${key}`}
          className={`lift group rounded-2xl border border-slate-200 bg-white p-6 ${i === 0 ? "lg:row-span-2 lg:bg-slate-900 lg:text-white lg:border-slate-900" : ""}`}>
          <span className={`h-12 w-12 rounded-2xl flex items-center justify-center text-white shadow-md bg-gradient-to-br ${ICON_COLORS[i % ICON_COLORS.length]} group-hover:scale-110 group-hover:rotate-3 transition-transform`} data-testid={`service-icon-${key}`}><Icon className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold">{t(`svc.${key}`)}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "lg:text-slate-300 text-slate-500" : "text-slate-500"}`}>{t(`svc.${key}_d`)}</p>
          <ArrowRight className="h-4 w-4 mt-4 text-teal-600 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
        </Link>
      ))}
    </div>
  );
}

function Flow() {
  const { t } = useI18n();
  return (
    <div className="grid md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className="relative rounded-2xl bg-white border border-slate-200 p-6" data-testid={`flow-step-${n}`}>
          <span className="font-mono text-4xl font-semibold text-teal-600/25">0{n}</span>
          <h3 className="mt-3 font-semibold text-slate-900">{t(`flow.${n}`)}</h3>
        </div>
      ))}
    </div>
  );
}

function Expect() {
  const { t } = useI18n();
  const items = [[ClipboardList, "e1"], [MessagesSquare, "e2"], [Camera, "e3"]];
  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
      <div className="rounded-3xl bg-teal-700 text-white p-8 relative overflow-hidden">
        <Users className="h-10 w-10 text-amber-400" />
        <p className="mt-5 text-2xl font-bold">{t("home.recs_t")}</p>
        <p className="mt-3 text-teal-50/90 leading-relaxed">{t("home.recs_d")}</p>
      </div>
      <div className="grid gap-4">
        {items.map(([Icon, k]) => (
          <div key={k} className="flex gap-4 rounded-2xl bg-white border border-slate-200 p-5">
            <Icon className="h-6 w-6 text-teal-600 shrink-0 mt-1" />
            <div><h3 className="font-semibold text-slate-900">{t(`home.${k}t`)}</h3><p className="text-sm text-slate-500 mt-1">{t(`home.${k}d`)}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UrgentCallout() {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 flex gap-4" data-testid="urgent-callout">
      <AlertTriangle className="h-7 w-7 text-red-600 shrink-0" />
      <div><p className="font-bold text-red-800">{t("urgent.title")}</p><p className="text-sm text-red-700 mt-1 leading-relaxed">{t("urgent.body")}</p></div>
    </div>
  );
}

function AreaCheck() {
  const { t } = useI18n();
  const [areas, setAreas] = useState([]);
  const [city, setCity] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setAreas(r.data.areas)); }, []);
  const check = () => {
    if (!city.trim()) return;
    const a = areas.find((x) => x.name.toLowerCase() === city.trim().toLowerCase());
    setResult(a ? (a.support_247 ? "yes247" : a.support_24h ? "yes24" : "yes") : "no");
  };
  return (
    <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center">
      <div><h2 className="text-2xl font-bold">{t("area.title")}</h2><p className="text-slate-300 mt-2">{t("area.body")}</p></div>
      <div>
        <div className="flex gap-2">
          <Input value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()} list="area-list"
            placeholder={t("area.placeholder")} className="bg-white text-slate-900 h-12" data-testid="area-check-input" />
          <datalist id="area-list">{areas.map((a) => <option key={a.name} value={a.name} />)}</datalist>
          <Button onClick={check} className="h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="area-check-btn"><Search className="h-4 w-4 me-1" />{t("area.check")}</Button>
        </div>
        {result && <p className="mt-4 text-sm text-teal-200" data-testid="area-check-result">{t(`area.${result}`, { city })}</p>}
      </div>
    </div>
  );
}

function EmergencyAsk() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <div className="rounded-3xl border-2 border-red-200 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5" data-testid="home-emergency-ask">
      <AlertTriangle className="h-10 w-10 text-red-600 shrink-0" />
      <div className="flex-1">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{t("emerg.q")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("emerg.home_d")}</p>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => nav("/request?urgent=1")} className="rounded-full bg-red-600 hover:bg-red-700 h-11 px-6 font-semibold" data-testid="home-emergency-yes">{t("yes")}</Button>
        <Button variant="outline" onClick={() => nav("/request")} className="rounded-full h-11 px-6" data-testid="home-emergency-no">{t("no")}</Button>
      </div>
    </div>
  );
}

function Help247() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <div className="relative overflow-hidden rounded-3xl bg-teal-700 text-white p-8 md:p-12 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center" data-testid="help-247-banner">
      <span className="font-mono text-5xl md:text-7xl font-semibold text-amber-400">24/7</span>
      <div>
        <h2 className="text-2xl font-bold">{t("home.247_t")}</h2>
        <p className="mt-2 text-teal-50/90 max-w-2xl leading-relaxed">{t("home.247_d")}</p>
      </div>
      <Button onClick={() => nav("/request?city=San%20Juan%20del%20Sur")} className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-12 px-6" data-testid="help-247-btn">{t("home.247_cta")}</Button>
    </div>
  );
}

function BuildBand() {
  const { t } = useI18n();
  return (
    <Link to="/build" className="group relative block overflow-hidden rounded-3xl min-h-[260px]" data-testid="home-build-band">
      <img src="/img/build-2.jpg" alt="Modern custom home with pool in Nicaragua" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-950/20" />
      <div className="relative p-8 md:p-12 text-white max-w-xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("b.home_t")}</h2>
        <p className="mt-2 text-slate-200">{t("b.home_d")}</p>
        <span className="mt-6 inline-flex items-center rounded-full bg-amber-400 text-slate-900 font-bold px-6 h-11" data-testid="home-build-btn">{t("dz.cta")}</span>
      </div>
    </Link>
  );
}

function CleaningBand() {
  const { t } = useI18n();
  const [cl, setCl] = useState({ packages: [{ price: 25 }, { price: 50 }] });
  useEffect(() => { api.get("/config").then((r) => r.data.cleaning && setCl(r.data.cleaning)); }, []);
  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 text-white p-8 md:p-12 flex flex-col md:flex-row items-center gap-6 md:justify-between text-center md:text-start" data-testid="home-cleaning-band">
      <Sparkles className="absolute -end-6 -top-6 h-40 w-40 text-white/10" />
      <div className="relative max-w-2xl">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("cl.home_t")}</h2>
        <p className="mt-2 text-blue-50">{t("cl.home_d", { p1: `$${cl.packages?.[0]?.price ?? ""}`, p2: `$${cl.packages?.[1]?.price ?? ""}` })}</p>
      </div>
      <Link to="/cleaning" className="relative shrink-0 inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-7 h-12 transition-colors" data-testid="home-book-maid-btn"><Sparkles className="h-4 w-4" />{t("cl.book")}</Link>
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();
  useSeo(t("hero.title"), t("hero.sub"));
  const S = ({ title, children }) => (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      {title && <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-10">{title}</h2>}
      {children}
    </section>
  );
  return (
    <div data-testid="home-page">
      <Hero />
      <S><EmergencyAsk /></S>
      <S><BuildBand /></S>
      <S><Help247 /></S>
      <S title={t("home.fix_title")}><ServiceGrid /></S>
      <S><CleaningBand /></S>
      <S title={t("home.how_title")}><Flow /></S>
      <S title={t("home.expect_title")}><Expect /></S>
      <S><UrgentCallout /></S>
      <S><AreaCheck /></S>
    </div>
  );
}
